/**
 * GroupsPage - Página de Grupos Premium
 * Migrado para padrão Premium Fleet
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import { useManager } from '../common/util/permissions';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { useEffectAsync } from '../reactHelper';
import useCrudExports from '../common/hooks/useCrudExports';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTableSelection } from '../common/hooks';

// Componentes extraídos
import GroupsTableRow from './components/GroupsTableRow';
import {
  PremiumStatCards,
  PremiumEmptyState,
  BulkActionsBar,
  BulkDeleteDialog,
  BulkConnectionsDialog,
} from '../common/components/premium';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LinkIcon from '@mui/icons-material/Link';

const GroupsPage = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const manager = useManager();

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  // Bulk action states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectionsDialogOpen, setConnectionsDialogOpen] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Fetch de dados
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow('/api/groups');
      const data = await response.json();
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  // Estatísticas com hierarquia
  const stats = useMemo(() => {
    const root = items.filter((i) => !i.groupId).length;
    const sub = items.filter((i) => i.groupId).length;
    return { total: items.length, root, sub };
  }, [items]);

  // Filtros para StatCards (usa tokens dinâmicos)
  const statFilters = useMemo(() => [
    { value: '', label: t('sharedAll'), count: stats.total, color: tokens?.accent?.primary, Icon: FolderIcon },
    { value: 'root', label: t('groupRoot') || 'Raiz', count: stats.root, color: tokens?.semantic?.success, Icon: FolderOpenIcon },
    { value: 'sub', label: t('groupSubgroups') || 'Subgrupos', count: stats.sub, color: tokens?.semantic?.info, Icon: AccountTreeIcon },
  ], [t, stats, tokens]);

  // Helper para obter nome do grupo pai
  const getParentName = useCallback((groupId) => {
    if (!groupId) return null;
    const parent = items.find((g) => g.id === groupId);
    return parent?.name || null;
  }, [items]);

  // Filtragem + Busca
  const filteredItems = useMemo(() => {
    let result = items;

    // Filtro por hierarquia
    if (statusFilter === 'root') {
      result = result.filter((i) => !i.groupId);
    } else if (statusFilter === 'sub') {
      result = result.filter((i) => i.groupId);
    }

    // Busca por texto
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (i) =>
          i.name?.toLowerCase().includes(lower) ||
          i.id?.toString().includes(lower) ||
          getParentName(i.groupId)?.toLowerCase().includes(lower)
      );
    }
    return result;
  }, [items, statusFilter, searchText, getParentName]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // Bulk selection hook
  const {
    selectedIds,
    selectedCount,
    toggleSelect,
    selectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    isIndeterminate,
    hasSelection,
  } = useTableSelection(filteredItems);

  // Bulk delete handler
  const handleBulkDelete = useCallback(async () => {
    setBulkActionLoading(true);
    try {
      // Delete each selected group
      await Promise.all(
        selectedIds.map((id) =>
          fetchOrThrow(`/api/groups/${id}`, { method: 'DELETE' })
        )
      );
      clearSelection();
      setDeleteDialogOpen(false);
      setTimestamp(Date.now()); // Refresh list
    } catch (error) {
      console.error('Erro ao excluir grupos:', error);
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedIds, clearSelection]);

  // Bulk custom actions
  const bulkCustomActions = useMemo(() => [
    {
      key: 'connections',
      label: t('sharedConnections') || 'Conexões',
      icon: <LinkIcon />,
      onClick: () => setConnectionsDialogOpen(true),
    },
  ], [t]);

  // Handlers
  const handleFilterChange = useCallback((value) => {
    setStatusFilter(value);
    setPage(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatusFilter('');
    setSearchText('');
    setPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    setTimestamp(Date.now());
  }, []);

  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  // Exportações
  const exportColumns = [
    { field: 'id', headerNameKey: 'sharedId' },
    { field: 'name', headerNameKey: 'sharedName' },
    {
      field: 'groupId',
      headerNameKey: 'groupParent',
      valueGetter: ({ row }) => getParentName(row.groupId) || '',
    },
  ];

  const { handleExportCsv, handleExportXlsx, handleExportPdf } = useCrudExports({
    titleKey: 'reportGroupsTitle',
    headerKey: 'reportGroupsHeader',
    rows: filteredItems,
    columns: exportColumns,
    t,
    options: { includeWatermark: true },
  });

  // Check if has active filters
  const hasFilters = Boolean(statusFilter || searchText.trim());

  // Header styles for premium table
  const headerCellSx = {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: tokens?.text?.secondary,
    borderBottom: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
    py: 1.5,
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'settingsGroups']}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Loading Skeleton */}
          {loading && items.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <Typography variant="body1" sx={{ color: tokens?.text?.secondary }}>
                {t('sharedLoading') || 'Carregando...'}
              </Typography>
            </Box>
          ) : (
            <>
              {/* CARDS DE ESTATÍSTICAS */}
              <PremiumStatCards
                filters={statFilters}
                activeFilter={statusFilter}
                onFilterChange={handleFilterChange}
              />

              {/* BARRA DE AÇÕES */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 1, sm: 1.5 },
                  alignItems: { xs: 'stretch', md: 'center' },
                  flexShrink: 0,
                }}
              >
                <TextField
                  size="small"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                  placeholder={t('sharedSearch')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: tokens?.accent?.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: { xs: '100%', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: tokens?.surface?.glass,
                      borderRadius: tokens?.radius?.md,
                      '&:hover': {
                        backgroundColor: tokens?.surface?.hover,
                      },
                    },
                  }}
                />
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    flexShrink: 0,
                    flexWrap: 'wrap',
                    gap: 0.5,
                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  }}
                >
                  <Button
                    variant="contained"
                    color="info"
                    size="small"
                    startIcon={<AddCircleIcon />}
                    onClick={() => navigate('/settings/group')}
                    sx={{
                      backgroundColor: tokens?.accent?.primary,
                      '&:hover': { backgroundColor: tokens?.accent?.hover },
                    }}
                  >
                    {t('sharedAdd')}
                  </Button>
                  {manager && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      endIcon={<ExpandMoreIcon />}
                      onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                      sx={{
                        color: tokens?.text?.primary,
                        borderColor: tokens?.border?.default,
                        borderRadius: tokens?.radius?.md,
                        fontWeight: 500,
                        textTransform: 'none',
                        transition: tokens?.transitions?.all,
                        '&:hover': {
                          borderColor: tokens?.accent?.primary,
                          bgcolor: `${tokens?.accent?.primary}10`,
                        },
                      }}
                    >
                      {t('reportExport') || 'Exportar'}
                    </Button>
                  )}
                </Stack>
              </Box>

              {/* Results count & Bulk Actions Bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredItems.length} {t('settingsGroups') || 'grupos'}
                  {hasFilters && ` (${t('sharedFiltered') || 'filtrado'})`}
                </Typography>

                {hasSelection && (
                  <BulkActionsBar
                    selectedCount={selectedCount}
                    onClearSelection={clearSelection}
                    onDelete={() => setDeleteDialogOpen(true)}
                    loading={bulkActionLoading}
                    entityName={t('settingsGroups') || 'grupo(s)'}
                    customActions={bulkCustomActions}
                  />
                )}
              </Box>

              {/* TABELA */}
              <Paper
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  borderRadius: tokens?.radius?.lg || '12px',
                  backgroundColor: tokens?.surface?.glass,
                  border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                }}
              >
                <TableContainer
                  className="premium-table-container"
                  sx={{ maxHeight: 600 }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {/* Checkbox Column */}
                        <TableCell padding="checkbox" sx={{ ...headerCellSx, width: 48 }}>
                          <Checkbox
                            indeterminate={isIndeterminate}
                            checked={isAllSelected}
                            onChange={selectAll}
                            sx={{
                              color: tokens?.text?.tertiary,
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                                color: tokens?.accent?.primary,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...headerCellSx, width: 48 }} />
                        <TableCell sx={{ ...headerCellSx, width: 70 }}>ID</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedName')}</TableCell>
                        <TableCell sx={headerCellSx}>{t('groupParent')}</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>{t('sharedActions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginated.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ border: 0, p: 0 }}>
                            <PremiumEmptyState
                              icon={FolderIcon}
                              title={t('groupsEmpty') || 'Nenhum grupo cadastrado'}
                              titleFiltered={t('groupsNotFound') || 'Nenhum grupo encontrado'}
                              subtitle={t('groupsEmptyHint') || 'Crie grupos para organizar seus dispositivos'}
                              subtitleFiltered={t('groupsNotFoundHint') || 'Tente ajustar os filtros de busca'}
                              hasFilters={hasFilters}
                              onClearFilters={handleClearFilters}
                              addPath="/settings/group"
                              addLabel={t('groupAdd') || 'Adicionar Grupo'}
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginated.map((row) => (
                          <GroupsTableRow
                            key={row.id}
                            row={row}
                            onRefresh={handleRefresh}
                            onNavigate={handleNavigate}
                            parentName={getParentName(row.groupId)}
                            selected={isSelected(row.id)}
                            onSelect={toggleSelect}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {filteredItems.length > 0 && (
                  <TablePagination
                    component="div"
                    count={filteredItems.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage={t('rowsPerPage') || 'Linhas por página'}
                    sx={{
                      borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontSize: '0.8rem',
                        color: tokens?.text?.secondary,
                      },
                    }}
                  />
                )}
              </Paper>
            </>
          )}
        </Box>
      </Box>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: tokens?.surface?.elevated,
            borderRadius: tokens?.radius?.lg,
            border: `1px solid ${tokens?.border?.subtle}`,
          },
        }}
      >
        <MenuItem
          onClick={() => { handleExportCsv(); setExportMenuAnchor(null); }}
          sx={{ color: tokens?.text?.primary }}
        >
          <FileDownloadIcon sx={{ mr: 1, fontSize: 18, color: tokens?.semantic?.success }} />
          CSV
        </MenuItem>
        <MenuItem
          onClick={() => { handleExportXlsx(); setExportMenuAnchor(null); }}
          sx={{ color: tokens?.text?.primary }}
        >
          <TableChartIcon sx={{ mr: 1, fontSize: 18, color: tokens?.semantic?.info }} />
          Excel
        </MenuItem>
        <MenuItem
          onClick={() => { handleExportPdf(); setExportMenuAnchor(null); }}
          sx={{ color: tokens?.text?.primary }}
        >
          <PictureAsPdfIcon sx={{ mr: 1, fontSize: 18, color: tokens?.semantic?.error }} />
          PDF
        </MenuItem>
      </Menu>

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        selectedCount={selectedCount}
        loading={bulkActionLoading}
        entityName={t('settingsGroups') || 'grupo(s)'}
      />

      {/* Bulk Connections Dialog */}
      <BulkConnectionsDialog
        open={connectionsDialogOpen}
        onClose={() => setConnectionsDialogOpen(false)}
        selectedIds={selectedIds}
        entityType="group"
        onSuccess={handleRefresh}
      />
    </PageLayout>
  );
};

export default GroupsPage;
